import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X, ChevronLeft, Globe, ShoppingBag, Smartphone,
  User, LayoutDashboard, Server, Mail, Wrench,
  ArrowRight, Sparkles, CheckCircle2
} from 'lucide-react'

const services = [
  {
    icon: Globe,
    title: "Business Website",
    desc: "Professional site for your shop, cafe, or service business. Mobile-first, fast-loading, built to convert visitors into customers.",
    tags: ["Design", "Development", "Hosting"],
    price: "From $1,200",
    popular: false,
  },
  {
    icon: Smartphone,
    title: "Digital Ordering App",
    desc: "Customers order directly from their phone at your venue. Staff manage orders in real time. Live example: Majnu Bar.",
    tags: ["Web App", "POS", "Real-time"],
    price: "From $2,500",
    popular: true,
  },
  {
    icon: ShoppingBag,
    title: "E-commerce Store",
    desc: "Sell products online with full payment processing, inventory management, and order notifications.",
    tags: ["Shopify", "Custom", "Payments"],
    price: "From $2,000",
    popular: false,
  },
  {
    icon: User,
    title: "Portfolio Website",
    desc: "A sharp personal site that showcases your work and gets you hired or booked. Like this one.",
    tags: ["Design", "React", "Fast"],
    price: "From $800",
    popular: false,
  },
  {
    icon: LayoutDashboard,
    title: "Custom Web App",
    desc: "CRM, booking systems, staff management, dashboards — any workflow turned into a clean, automated tool.",
    tags: ["React", "FastAPI", "MongoDB"],
    price: "Custom quote",
    popular: false,
  },
  {
    icon: Server,
    title: "IT Store Setup",
    desc: "Full physical IT for your premises — CCTV, security alarms, PA system, structured cabling, and POS — ready on day one.",
    tags: ["CCTV", "POS", "Cabling"],
    price: "From $1,500",
    popular: false,
  },
  {
    icon: Mail,
    title: "Domain & Business Email",
    desc: "Get your own domain (yourbusiness.com.au) and professional email (you@yourbusiness.com.au) set up correctly.",
    tags: ["Domain", "Email", "Google"],
    price: "From $200/yr",
    popular: false,
  },
  {
    icon: Wrench,
    title: "Managed IT Support",
    desc: "Monthly IT maintenance and priority support — proactive checks, hardware lifecycle, rapid response when things go wrong.",
    tags: ["Support", "Maintenance", "Priority"],
    price: "From $299/mo",
    popular: false,
  },
]

const included = [
  "Professional consultation before any work starts",
  "Clear quote with no hidden fees",
  "You own everything built for you",
  "Ongoing support available after launch",
]

export default function ServicesDrawer({ onNavigate, onSelectService, onBookConsultation }) {
  const [open, setOpen] = useState(false)

  return (
    <>
      {/* Prominent sticky side tab — DESKTOP ONLY (lg+) */}
      <motion.button
        initial={{ opacity: 0, x: 60 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 2, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        onClick={() => setOpen(true)}
        data-testid="services-tab"
        aria-label="View services and pricing"
        className={`fixed right-0 top-[40%] -translate-y-1/2 z-[150] hidden lg:flex flex-col items-center gap-2.5 rounded-l-2xl transition-all duration-200 ${
          open ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
        style={{
          background: 'linear-gradient(180deg, #0047AB 0%, #0055CC 100%)',
          boxShadow: '-4px 0 24px rgba(0,71,171,0.28), -2px 0 8px rgba(0,71,171,0.15)',
          padding: '18px 12px',
        }}
      >
        <span className="bg-white text-[#0047AB] font-mono text-[9px] font-bold px-2 py-0.5 rounded-full tracking-wide">
          $8.99
        </span>
        <span
          className="font-sans font-bold text-white text-[11px] tracking-[0.18em] uppercase select-none"
          style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', lineHeight: 1.2 }}
        >
          Start a Project
        </span>
        <ChevronLeft size={14} className="text-white/80" />
        <motion.span
          className="absolute inset-0 rounded-l-2xl"
          style={{ background: 'linear-gradient(180deg, #0047AB 0%, #0055CC 100%)' }}
          animate={{ opacity: [0.4, 0, 0.4] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
        />
      </motion.button>

      {/* Mobile bottom CTA bar — shows only on < lg */}
      <motion.div
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 2, duration: 0.5 }}
        className={`fixed bottom-0 left-0 right-0 z-[150] lg:hidden transition-opacity duration-200 ${open ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
      >
        <div className="bg-white/95 backdrop-blur-sm border-t border-[#E5E7EB] px-4 pt-3 pb-4">
          <button
            onClick={() => { setOpen(true) || (onBookConsultation && onBookConsultation()) }}
            data-testid="services-tab-mobile"
            aria-label="Start a project"
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white text-sm font-semibold"
            style={{ background: 'linear-gradient(135deg, #0047AB 0%, #0055CC 100%)' }}
          >
            <Sparkles size={14} />
            Start a Project — $8.99 consultation
          </button>
        </div>
      </motion.div>

      {/* Backdrop */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[155] bg-black/20 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Drawer */}
      <AnimatePresence>
        {open && (
          <motion.aside
            key="drawer"
            data-testid="services-drawer"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.38, ease: [0.16, 1, 0.3, 1] }}
            className="fixed right-0 top-0 bottom-0 z-[160] w-full max-w-[460px] bg-white border-l border-[#E5E7EB] shadow-float flex flex-col"
            role="dialog"
            aria-label="Services panel"
          >
            {/* Header */}
            <div className="px-6 pt-6 pb-5 border-b border-[#F3F4F6] shrink-0">
              <div className="flex items-start justify-between mb-3">
                <div className="p-1.5 bg-[#EBF2FF] border border-[#0047AB]/15 rounded-lg">
                  <Sparkles size={14} className="text-[#0047AB]" />
                </div>
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Close services panel"
                  data-testid="services-close"
                  className="p-2 text-[#9CA3AF] hover:text-[#374151] hover:bg-[#F3F4F6] rounded-lg transition-colors duration-150"
                >
                  <X size={15} />
                </button>
              </div>
              <h2 className="font-sans font-bold text-[#111111] text-xl tracking-tight mb-1">
                Build with Akshay
              </h2>
              <p className="font-body text-[#6B7280] text-sm leading-relaxed">
                From getting your domain to launching a full app — one person, full accountability, no agency markup.
              </p>
            </div>

            {/* Services scroll area */}
            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-3">
              {services.map((service, i) => (
                <ServiceCard key={service.title} service={service} index={i} />
              ))}

              {/* What's always included */}
              <div className="mt-6 p-5 border border-[#E5E7EB] rounded-2xl bg-[#FAFAFA]">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] mb-4">
                  Always included
                </div>
                <ul className="space-y-2.5">
                  {included.map((item) => (
                    <li key={item} className="flex items-start gap-2.5">
                      <CheckCircle2 size={13} className="text-[#0047AB] mt-0.5 shrink-0" />
                      <span className="font-body text-[#374151] text-xs leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Footer CTA */}
            <div className="px-5 py-5 border-t border-[#F3F4F6] shrink-0 space-y-3">
              <button
                onClick={() => { setOpen(false); onBookConsultation ? onBookConsultation() : (onSelectService ? onSelectService('general enquiry') : onNavigate('contact')) }}
                data-testid="services-contact-cta"
                className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-[#111111] text-white text-sm font-semibold rounded-xl hover:bg-[#0047AB] transition-colors duration-200"
              >
                Book a consultation — $8.99
                <ArrowRight size={14} />
              </button>
              <p className="text-center font-mono text-[10px] text-[#9CA3AF]">
                30-min strategy call · No lock-in · Get a clear plan
              </p>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  )
}

function ServiceCard({ service, index }) {
  const Icon = service.icon
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.04, duration: 0.3 }}
      className="group flex gap-4 p-4 border border-[#E5E7EB] rounded-xl bg-white hover:border-[#0047AB]/25 hover:bg-[#FAFAFA] transition-all duration-200 cursor-default"
      data-testid={`service-card-${index}`}
    >
      {/* Icon */}
      <div className="shrink-0 p-2.5 bg-[#EBF2FF] border border-[#0047AB]/12 rounded-xl group-hover:bg-[#0047AB] group-hover:border-[#0047AB] transition-all duration-200">
        <Icon size={15} className="text-[#0047AB] group-hover:text-white transition-colors duration-200" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-sans font-semibold text-[#111111] text-sm">{service.title}</span>
          {service.popular && (
            <span className="px-1.5 py-0.5 bg-[#EBF2FF] text-[#0047AB] text-[9px] font-mono uppercase tracking-wider rounded-full border border-[#0047AB]/20">
              Popular
            </span>
          )}
        </div>
        <p className="font-body text-[#6B7280] text-xs leading-relaxed mb-2">{service.desc}</p>
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap gap-1">
            {service.tags.map(tag => (
              <span key={tag} className="px-1.5 py-0.5 text-[#9CA3AF] text-[9px] font-mono border border-[#E5E7EB] rounded bg-[#F3F4F6]">
                {tag}
              </span>
            ))}
          </div>
          <span className="font-mono text-[#0047AB] text-[11px] shrink-0 ml-2">{service.price}</span>
        </div>
      </div>
    </motion.div>
  )
}
